/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import util from '../../../../share/util/utils';
import { apiErrorDefaultAction } from '../../../../share/util/api/errorAction';
import { PHASE, PHASE_INFO } from './constants';
import { executeApi, handleResponseType, handleRatio } from './requirement064Api';

export default {
  name: 'requirement064',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      ratioInfo: '',
      systemId: '',
      isTimer: false,
      isTimeout: false,
      timer: null,
      sessionTransitionFlag: false,
      modal_requirement_064: false,
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),
    // ダイアログのヘッダー色
    headerColor() { return PHASE_INFO[this.phase]['headerColor']; },
    // ダイアログヘッダ―文言
    headerTitle() { return this.$t(PHASE_INFO[this.phase]['headerTitle']); },

    workSupportMessage() {
      const messageMap = {
        [PHASE.START]: '',
        [PHASE.CAUTION]: `${this.i18nWsKey}.REQ_WS_64_TXT_1`,
        [PHASE.NONCONFORMITY]: `${this.i18nWsKey}.REQ_WS_64_TXT_2`,
        [PHASE.READY_ON_CONFIRMATION]: `${this.i18nWsKey}.REQ_WS_64_TXT_3`,
        [PHASE.FAULT_DETECTION]: `${this.i18nWsKey}.REQ_WS_64_TXT_4`,
        [PHASE.RELEASE_PARKING_BRAKE]: `${this.i18nWsKey}.REQ_WS_64_TXT_5`,
        [PHASE.STEERING_CHK]: `${this.i18nWsKey}.REQ_WS_64_TXT_6`,
        [PHASE.LIFT_UP_CHK]: `${this.i18nWsKey}.REQ_WS_64_TXT_7`,
        [PHASE.WARN_INTERRUPTION]: `${this.i18nWsKey}.REQ_WS_64_TXT_8`,
        [PHASE.VDC_OFF_CONFIRMATION]: `${this.i18nWsKey}.REQ_WS_64_TXT_9`,
        [PHASE.PUSH_BRAKE]: `${this.i18nWsKey}.REQ_WS_64_TXT_10`,
        [PHASE.D_RANGE_SELECT]: `${this.i18nWsKey}.REQ_WS_64_TXT_11`,
        [PHASE.STOP_LAMP_INFO]: `${this.i18nWsKey}.REQ_WS_64_TXT_12`,
        [PHASE.INSPECTION_ERROR]: `${this.i18nWsKey}.REQ_WS_64_TXT_16`,
        [PHASE.STOP_VEHICLE]: `${this.i18nWsKey}.REQ_WS_64_TXT_17`,
        [PHASE.FINISH_INSPECTION]: `${this.i18nWsKey}.REQ_WS_64_TXT_18`,
        [PHASE.IG_OFF]: `${this.i18nWsKey}.REQ_WS_64_TXT_19`,
        [PHASE.TIME_OUT_NOT_CHK]: `${this.i18nWsKey}.REQ_WS_64_TXT_20`,
        [PHASE.TIME_OUT_CHK]: `${this.i18nWsKey}.REQ_WS_64_TXT_20`,
        [PHASE.CANCEL]: `${this.i18nWsKey}.REQ_WS_64_TXT_21`,
        [PHASE.FAILED]: 'individual.error_communication_error'
      };
      if (this.phase === PHASE.UNDER_INSPECTION
        || this.phase === PHASE.SUCCESS
        || this.phase === PHASE.REAR_DIFF_ERROR) {
        return this.ratioInfo;
      }
      return this.$t(messageMap[this.phase]);
    },

    showButtonOk() {
      const showOkButtonMap = {
        [PHASE.CAUTION]: true,
        [PHASE.NONCONFORMITY]: true,
        [PHASE.READY_ON_CONFIRMATION]: true,
        [PHASE.FAULT_DETECTION]: true,
        [PHASE.RELEASE_PARKING_BRAKE]: true,
        [PHASE.STEERING_CHK]: true,
        [PHASE.LIFT_UP_CHK]: true,
        [PHASE.WARN_INTERRUPTION]: true,
        [PHASE.VDC_OFF_CONFIRMATION]: true,
        [PHASE.SUCCESS]: true,
        [PHASE.REAR_DIFF_ERROR]: true,
        [PHASE.INSPECTION_ERROR]: true,
        [PHASE.FINISH_INSPECTION]: true,
        [PHASE.IG_OFF]: true,
        [PHASE.TIME_OUT_NOT_CHK]: true,
        [PHASE.TIME_OUT_CHK]: true,
        [PHASE.CANCEL]: true,
        [PHASE.FAILED]: true
      };
      let result = showOkButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    },

    showButtonCancel() {
      const showCancelButtonMap = {
        [PHASE.CAUTION]: true,
        [PHASE.READY_ON_CONFIRMATION]: true,
        [PHASE.RELEASE_PARKING_BRAKE]: true,
        [PHASE.STEERING_CHK]: true,
        [PHASE.LIFT_UP_CHK]: true,
        [PHASE.WARN_INTERRUPTION]: true,
        [PHASE.VDC_OFF_CONFIRMATION]: true,
      };
      let result = showCancelButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    },
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_requirement_064 = true;
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
    },

    /** Wizardを閉じる */
    closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_064 = false;
    },

    /** キャンセルボタンクリック時の処理 */
    async onCancelClicked() {
      switch (this.phase) {
        case PHASE.READY_ON_CONFIRMATION:
        case PHASE.RELEASE_PARKING_BRAKE:
          await this.igOffProcess(); // IG OFF 指示へ遷移
          break;
        case PHASE.CAUTION:
        case PHASE.STEERING_CHK:
        case PHASE.LIFT_UP_CHK:
        case PHASE.WARN_INTERRUPTION:
        case PHASE.VDC_OFF_CONFIRMATION:
          this.phase = PHASE.CANCEL; // 中断画面へ遷移
          break;
        default:
          break;
      }
    },

    /** OKボタンクリック時の処理 */
    async onOkClicked() {
      switch (this.phase) {
        case PHASE.CAUTION:
          this.phase = PHASE.READY_ON_CONFIRMATION; // Ready ON 確認へ遷移
          break;
        case PHASE.READY_ON_CONFIRMATION:
          await executeApi(this, 'read_wait_time-ready_on_flg',
            // status: completed 時処理
            async (self, response) => {
              await handleResponseType(self, response,
                async (self) => { await self.getCallTempFailureCodeFlg(); }, // res_type: ok で SSM 用仮故障コード確認フラグ情報の取得へ遷移
                (self) => { self.phase = PHASE.TIME_OUT_NOT_CHK; }           // res_type: ng でタイムアウトエラー(中断確認なし)へ遷移
              );
            },
            // status: failed 時処理
            async (self) => { self; await this.igOffProcess(); } // status: failed で IG OFF 指示へ遷移
          );
          break;
        case PHASE.RELEASE_PARKING_BRAKE:
          this.phase = PHASE.STEERING_CHK; // ステアリング状態確認へ遷移
          break;
        case PHASE.STEERING_CHK:
          this.phase = PHASE.LIFT_UP_CHK; // リフトアップ確認へ遷移
          break;
        case PHASE.LIFT_UP_CHK:
          this.phase = PHASE.WARN_INTERRUPTION; // 点検中断警告表示へ遷移
          break;
        case PHASE.WARN_INTERRUPTION:
          this.phase = PHASE.VDC_OFF_CONFIRMATION; // VDC OFF 確認へ遷移
          break;
        case PHASE.VDC_OFF_CONFIRMATION:
          this.phase = PHASE.PUSH_BRAKE; // ブレーキペダル踏み込み指示へ遷移
          await executeApi(this, 'read_wait_time-push_brake_pedal',
            // status: completed 時処理
            async (self, response) => {
              await handleResponseType(self, response,
                async (self) => { await self.getCallDRangeSelect(); }, // res_type: ok で D レンジセレクト指示へ遷移
                (self) => { self.phase = PHASE.TIME_OUT_CHK; }         // res_type: ng でタイムアウトエラー(中断確認あり)へ遷移
              );
            },
          );
          break;
        case PHASE.SUCCESS:
        case PHASE.REAR_DIFF_ERROR:
        case PHASE.INSPECTION_ERROR:
          this.phase = PHASE.STOP_VEHICLE;
          await executeApi(this, 'read_wait_time-stop_vehicle',
            // status: completed 時処理
            async (self, response) => {
              await handleResponseType(self, response,
                async (self) => { await self.getCallNormalStop(); },  // res_type: ok で通常停止へ遷移
                (self) => { self.phase = PHASE.TIME_OUT_CHK; }        // res_type: ng でタイムアウトエラー(中断確認あり)へ遷移
              );
            },
          );
          break;
        case PHASE.NONCONFORMITY:
        case PHASE.CANCEL:
        case PHASE.TIME_OUT_NOT_CHK:
        case PHASE.FAULT_DETECTION:
        case PHASE.FINISH_INSPECTION:
          await this.igOffProcess(); // IG OFF 指示へ遷移
          break;
        case PHASE.IG_OFF:
          this.closeWizard(); // 機能終了
          break;
        case PHASE.TIME_OUT_CHK:
          this.phase = PHASE.CANCEL; // 中断画面へ遷移
          break;
        case PHASE.FAILED:
          await this.exitProcess(); // 終了処理を実行
          break;
        default:
          break;
      }
    },

    /**
     * SSM用仮故障コード確認フラグ情報の取得処理
     */
    async getCallTempFailureCodeFlg() {
      await util.sleep(2000);
      await executeApi(this, 'read_with_session_transition',
        // status: completed 時処理
        async (self, response) => {
          await handleResponseType(self, response,
            async (self) => { self.phase = PHASE.RELEASE_PARKING_BRAKE; }, // res_type: ok でパーキングブレーキ解除確認へ遷移
            (self) => { self.phase = PHASE.FAULT_DETECTION; }              // res_type: ng で故障検出表示へ遷移
          );
        },
      );
    },

    /**
     * Dレンジセレクト指示処理
     */
    async getCallDRangeSelect() {
      this.phase = PHASE.D_RANGE_SELECT;
      await executeApi(this, 'read_wait_time-d_range_select',
        // status: completed 時処理
        async (self, response) => {
          await handleResponseType(self, response,
            async (self) => { await self.writeCallRearDiffMismatchDetectionData(); }, // res_type: ok で SSM 用リヤデフ誤組検出データ書込みへ遷移
            (self) => { self.phase = PHASE.TIME_OUT_CHK; }                             // res_type: ng でタイムアウトエラー(中断確認あり)へ遷移
          );
        },
      );
    },

    /**
     * SSM用リヤデフ誤組検出データ書込み処理
     */
    async writeCallRearDiffMismatchDetectionData() {
      await executeApi(this, 'write-rear_diff_mismatch',
        // status: completed 時処理
        async () => { await this.getCallStopLampInfo(); }, // status: completed でストップランプ SW 情報の取得へ遷移
        // status: failed 時処理
        () => { this.showUnexpectedError(); } // status: failed で共通エラーへ遷移
      );
    },

    /**
     * ストップランプSW情報の取得処理
     */
    async getCallStopLampInfo() {
      this.phase = PHASE.STOP_LAMP_INFO;
      await executeApi(this, 'read_wait_time-stop_lamp',
        // status: completed 時処理
        async (self, response) => {
          await handleResponseType(self, response,
            async (self) => { await self.getCallWheelRotationRatioData(); }, // res_type: ok で前後輪回転比情報の取得へ遷移
            (self) => { self.phase = PHASE.TIME_OUT_CHK; }                    // res_type: ng でタイムアウトエラー(中断確認あり)へ遷移
          );
        },
      );
    },

    /**
     * 前後輪回転比情報の取得
     */
    async getCallWheelRotationRatioData() {
      // タイムアウト時は通信しない
      if (this.isTimeout) return;

      await executeApi(this, 'read-wheel_rotation_ratio',
        // status: completed 時処理
        async (self, response) => {
          // タイムアウト時は中断する
          if (this.isTimeout) return;

          await handleRatio(self, response, 'REQ_WS_64_TXT_13',
            async (self) => {
              self.phase = PHASE.UNDER_INSPECTION; // status: completed で点検中表示へ遷移
              await self.readUnderInspection();
            }
          );
        },
      );
    },

    /**
     * 点検中表示時の処理
     */
    async readUnderInspection() {
      await executeApi(this, 'read-under_inspection',
        // status: completed 時処理
        async (self, response) => {
          switch (response.data.user_data.result) {
            case 'under_inspection':
              await self.checkTimer(self);
              break;
            case 'complete_inspection':
              await self.resetTimer(self);
              break;
          }
        },
      );
    },

    /**
     * 点検中表示時のタイマー処理
     */
    async checkTimer() {
      if (!this.isTimer) {
        this.isTimer = true;
        this.timer = setTimeout(() => this.execTimeout(), 60000);
      }
      if (!this.isTimeout) {
        // 前後輪回転比情報の取得を実行
        await this.getCallWheelRotationRatioData();
      }
    },

    /**
     * 点検中表示完了後のタイマーリセット処理
     */
    async resetTimer() {
      // タイムアウト処理実行確認
      if (this.timer) {
        // タイムアウト処理が実行されていた場合、タイマーをリセットする
        clearTimeout(this.timer);
        this.timer = null;
        this.isTimer = false;
      }
      // 点検データの取得を実行
      await this.readInspectionData();
    },

    /**
     * 点検データの取得処理
     */
    async readInspectionData() {
      await executeApi(this, 'read-inspection_data',
        // status: completed 時処理
        async (self, response) => {
          switch (response.data.user_data.result) {
            case 'success':
              await self.getCallSuccess();
              break;
            case 'rear_diff_failed':
              await self.getCallRearDiffError();
              break;
            case 'unexpected':
              self.phase = PHASE.INSPECTION_ERROR;
              break;
          }
        },
      );
    },

    /**
     * 正常終了の表示時の処理
     */
    async getCallSuccess() {
      await executeApi(this, 'read-success',
        // status: completed 時処理
        async (self, response) => {
          await handleRatio(self, response, 'REQ_WS_64_TXT_14',
            (self) => { self.phase = PHASE.SUCCESS; } // status: completed で点検中表示へ遷移            
          );
        },
      );
    },

    /**
     * リヤデフ異常の表示時の処理
     */
    async getCallRearDiffError() {
      await executeApi(this, 'read-rear_diff_error',
        // status: completed 時処理
        async (self, response) => {
          await handleRatio(self, response, 'REQ_WS_64_TXT_15',
            (self) => { self.phase = PHASE.REAR_DIFF_ERROR; } // status: completed で点検中表示へ遷移            
          );
        },
      );
    },

    /**
     * 通常停止時の処理
     */
    async getCallNormalStop() {
      await executeApi(this, 'write-normal_stop',
        // status: completed 時処理
        () => { this.phase = PHASE.FINISH_INSPECTION; }, // status: completed で終了表示へ遷移
      );
    },

    /**
     * IGOFF指示処理
     */
    async igOffProcess() {
      if (this.sessionTransitionFlag) {
        await executeApi(this, 'exit',
          // status: completed 時処理
          () => { this.phase = PHASE.IG_OFF; },  // status: completed で IG OFF 指示へ遷移
        );
      } else {
        await executeApi(this, 'dispose',
          // status: completed 時処理
          () => { this.phase = PHASE.IG_OFF; },
          // status: failed 時処理
          () => { this.phase = PHASE.IG_OFF; } // 結果に関わらず IG OFF 指示へ遷移
        );
      }
    },

    /**
     * エラー時の終了処理
     */
    async exitProcess() {
      if (this.sessionTransitionFlag) {
        await executeApi(this, 'exit',
          // status: completed 時処理
          () => { this.closeWizard(); },
          // status: failed 時処理
          () => { this.closeWizard(); }, // 結果に関わらず機能終了
        );
      } else {
        await executeApi(this, 'dispose',
          // status: completed 時処理
          () => { this.closeWizard(); },
          // status: failed 時処理
          () => { this.closeWizard(); }, // 結果に関わらず機能終了
        );
      }
    },

    /** タイムアウト処理 */
    execTimeout() {
      clearTimeout(this.timer);
      this.timer = null;
      this.isTimer = false;
      this.isTimeout = true;
      this.phase = PHASE.TIME_OUT_CHK;
      return;
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      // 異常終了：エラー表示
      this.phase = PHASE.FAILED;
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },
  },

  async mounted() {
    this.init();
    // 処理実行
    await executeApi(this, 'read-start',
      // status: completed 時処理
      async (self, response) => {
        await handleResponseType(self, response,
          (self) => { self.phase = PHASE.CAUTION; },       // result: ok で注意表示へ遷移
          (self) => { self.phase = PHASE.NONCONFORMITY; }, // result: ng でユニット不適合の表示へ遷移
        );
      },
    );
  },
};
